import { useState } from "react";
import {
  Button,
  Divider,
  ListItemButton,
  ListItemIcon,
  Menu,
  Stack,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { CheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useFormat } from "hooks/useFormat";

const ListIcon = styled(ListItemIcon)({
  minWidth: "28px",
});

type Interval = { start: Date; end: Date };

type Value = Interval & { type: string };

type DateRangePickerProps = {
  value: Value;
  onChange: (value: Value) => void;
  options: {
    key: string;
    label: string;
    value: Interval;
  }[];
  dateFormatStr?: string;
};

export function DateRangePicker({
  value,
  onChange,
  options,
  dateFormatStr = "PPP",
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const { format } = useFormat();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [showCustom, setShowCustom] = useState(value.type === "custom");

  const [customValue, setCustomValue] = useState<Value>({
    ...value,
    type: "custom",
  });

  const getDateLabel = (value: Interval) => {
    const from = format(value.start, dateFormatStr);
    const to = format(value.end, dateFormatStr);
    return `${from} - ${to}`;
  };

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        variant="outlined"
      >
        {getDateLabel(value)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: "350px" } } }}
      >
        {options.map((option) => (
          <ListItemButton
            key={option.key}
            selected={value.type === option.key}
            onClick={() => {
              onChange({ ...option.value, type: option.key });
              setShowCustom(false);
              setAnchorEl(null);
            }}
          >
            <ListIcon>{value.type === option.key && <CheckIcon />}</ListIcon>
            {option.label}
          </ListItemButton>
        ))}
        <ListItemButton
          selected={value.type === "custom"}
          onClick={() => setShowCustom(true)}
        >
          <ListIcon>{value.type === "custom" && <CheckIcon />}</ListIcon>
          {t("custom")}
        </ListItemButton>
        {showCustom && (
          <div>
            <Divider />
            <Stack sx={{ gap: 1, paddingBlockStart: 2, paddingInline: 2 }}>
              <Stack
                direction={"row"}
                sx={{
                  gap: 1,
                }}
              >
                <DatePicker
                  onChange={(value) =>
                    value && setCustomValue((val) => ({ ...val, start: value }))
                  }
                  value={customValue.start}
                  maxDate={customValue.end ?? new Date()}
                />
                <DatePicker
                  onChange={(value) =>
                    value && setCustomValue((val) => ({ ...val, end: value }))
                  }
                  value={customValue.end}
                  minDate={customValue.start ?? undefined}
                  maxDate={new Date()}
                />
              </Stack>
              <Button
                variant={"contained"}
                onClick={() => {
                  onChange(customValue);
                  setAnchorEl(null);
                }}
                sx={{ alignSelf: "end" }}
              >
                {t("apply_filter")}
              </Button>
            </Stack>
          </div>
        )}
      </Menu>
    </>
  );
}
